import React from 'react'
import {
  IconCaretDown,
  IconCaretRight,
  IconCheckMark,
  IconClock,
  IconClockSolid,
  IconEdit,
  IconGlobe,
  IconLocation,
  IconPlus,
  IconStickyNote,
  IconTimes,
  IconVideo,
} from '../../../icons/library'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import { Badge, BadgeStyle } from '../../../components/simple/badge'
import { parseISOWithTimezoneFallback } from '../../../components/simple/date'
import { BasicUser } from '../../../components/userSelector/users'
import { isPast } from 'date-fns'
import { useTranslation } from '../../../providers/translation'
import { Popups } from '../../../providers/popups'
import { HoverPopover, Popover } from '../../../layouts/components/popover'
import { SimpleUser, User } from '../../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../../request'
import { formatInTimeZone } from 'date-fns-tz'
import { TextArea } from '../../../components/form/textInput'
import { StudentRecipient } from '../../home/compose/composeTypes'
import { getComposeLink } from '../../../providers/URLParams'
import { SmartLinkOptional } from '../../../components/simple/link'

export type ConferenceLocation = { url?: string; roomNumber?: string; googleOAuth?: boolean; lexikeet?: boolean }

export type SimpleUserWithLocation = SimpleUser & { location: ConferenceLocation }

export type ConferenceAppointment = {
  id: number
  student: SimpleUser
  teacher: SimpleUserWithLocation
  languageName?: string
  attended: boolean
  virtual: boolean
  note?: string
  guardians: { name: string; phone: string; relationship: string }[]
  multiple: boolean
  location: ConferenceLocation
  tooSoonToDeleteLexikeet: boolean
}

export type ConferenceTime = {
  id: number
  date: string
  canSchedule: boolean
  cantScheduleMessage?: string
  markedUnavailable?: {
    note?: string
    appointmentID: number
  }
  scheduled: boolean
  appointments: ConferenceAppointment[]
  appointmentsInFamily: ConferenceAppointment[]
  appointmentsForTeacher: ConferenceAppointment[]
  interpretersAvailable: Record<string, number>
  missingInterpreters: boolean
}

export type ConferenceTimeRange = {
  date: string
  startTime: string
  endTime: string
  editable: boolean
}

export type SharedConferenceData = {
  id: number
  name: string
  active: boolean
  hasEnded: boolean
  days: string[]
  timezone: string
  interpreterLanguages?: string[]
  virtualOrInPerson: 'virtual' | 'inperson' | 'both'
  multilingual: boolean
  duration: number
  buffer: number
}

export type TeacherConferenceData = SharedConferenceData & {
  canBeEdited: boolean
  adminLed: boolean
  yourLocation: ConferenceLocation
  waitingForAdminToInvite: boolean
  school: {
    id: number
    name: string
    grades: string[]
  }
  preloadedRecipients: StudentRecipient[]
  defaultInviteMessage: string
  teachersViewAllAppointments: boolean
}

export const ConferenceTime: React.FC<{
  conference: SharedConferenceData
  time: ConferenceTime
  viewAsAdmin?: boolean
  viewAsOtherTeacher?: SimpleUserWithLocation
  selectedLanguage?: { name: string; value: string }
  user: User
  reloadSchedule: () => void
  scheduleAppointment: () => void
}> = ({
  conference,
  time,
  viewAsAdmin,
  user,
  reloadSchedule,
  scheduleAppointment,
  selectedLanguage,
  viewAsOtherTeacher,
}) => {
  const [showAppointments, setShowAppointments] = React.useState(false)
  React.useEffect(() => {
    if (time.appointments.length >= 5) setShowAppointments(false)
  }, [time.appointments.length])
  const shouldShowAppointments = showAppointments || time.appointments.length < 5

  const date = React.useMemo(() => parseISOWithTimezoneFallback(time.date), [time.date])
  const { tr } = useTranslation()

  const [loading, setLoading] = React.useState(false)

  const showDividers =
    user.type === 'teacher' ||
    (time.appointments.length + time.appointmentsInFamily.length === 0 && !time.markedUnavailable)

  const setAvailability = (available: boolean) => {
    if (viewAsAdmin && !viewAsOtherTeacher) return
    setLoading(true)
    makeRequest('/ptc/markavailability', {
      time_id: time.id,
      unavailable: !available,
      teacherID: viewAsAdmin ? viewAsOtherTeacher!.id : undefined,
    })
      .then(() => {
        reloadSchedule()
      })
      .catch(showAxiosErrorAsToast)
      .finally(() => setLoading(false))
  }
  return (
    <div
      className={`flex gap-3 rounded-[5px] ${time.markedUnavailable ? 'bg-slate-100' : 'bg-white'} p-3 shadow-even ${loading ? 'pointer-events-none opacity-50' : ''}`}
    >
      <div className="flex flex-col">
        <b className="text-nowrap">{formatInTimeZone(date, conference.timezone, 'h:mm a')}</b>
        {conference.buffer > 0 && <>({conference.duration} min)</>}
      </div>
      {showDividers && <div className="w-px self-stretch bg-slate-100 -sm:hidden"></div>}

      {time.markedUnavailable ? (
        <div className="flex-grow">
          <b className="mr-2">{tr('Unavailable')}</b>
          {user.type === 'teacher' && (
            <NoteEditor note={time.markedUnavailable.note} appointmentID={time.markedUnavailable.appointmentID} />
          )}
        </div>
      ) : (
        <>
          <div className="-m-0.5 flex min-w-0 flex-grow flex-col gap-2 overflow-hidden p-0.5">
            {time.appointments.length >= 5 && (
              <div
                className="flex cursor-pointer select-none items-center justify-between rounded bg-slate-50 px-2 py-1 shadow-even"
                onClick={() => setShowAppointments((c) => !c)}
              >
                <b>{time.appointments.length} appointments</b>
                {shouldShowAppointments ? <IconCaretDown /> : <IconCaretRight />}
              </div>
            )}
            {time.appointments.map((appointment) => (
              <ConferenceAppointment
                key={appointment.id}
                conference={conference}
                appointment={appointment}
                viewAsAdmin={viewAsAdmin}
                user={user}
                className={`bg-slate-50 ${shouldShowAppointments ? '' : 'hidden'}`}
                reloadSchedule={reloadSchedule}
              />
            ))}
            {time.appointmentsInFamily.length + time.appointmentsForTeacher.length > 0 && (
              <div className="flex gap-2">
                {time.appointmentsInFamily.length > 0 && (
                  <div className="flex min-w-0 flex-grow flex-col">
                    {time.appointmentsInFamily.map((appointment) => (
                      <ConferenceAppointment
                        key={appointment.id}
                        conference={conference}
                        appointment={appointment}
                        viewAsAdmin={viewAsAdmin}
                        user={user}
                        className="bg-slate-200"
                        canEdit={false}
                        reloadSchedule={reloadSchedule}
                      />
                    ))}
                  </div>
                )}
                {time.appointmentsForTeacher.length > 0 && (
                  <div className="flex min-w-0 flex-grow flex-col">
                    {time.appointmentsForTeacher.map((appointment) => (
                      <ConferenceAppointment
                        key={appointment.id}
                        conference={conference}
                        appointment={appointment}
                        viewAsAdmin={viewAsAdmin}
                        user={user}
                        canEdit={false}
                        className="bg-slate-200"
                        reloadSchedule={reloadSchedule}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
      {showDividers && <div className="w-px self-stretch bg-slate-100 -sm:hidden"></div>}
      {((!isPast(date) &&
        conference.active &&
        !time.markedUnavailable &&
        (time.canSchedule || time.appointments.length + time.appointmentsInFamily.length === 0)) ||
        user.type === 'teacher') && (
        <Button
          buttonStyle={ButtonStyle.blue}
          shape={user.type === 'guardian' ? ButtonShape.roundedSmall : ButtonShape.iconSmall}
          disabled={!time.canSchedule}
          tooltip={time.canSchedule ? undefined : time.cantScheduleMessage}
          tooltipOnMobile
          onClick={scheduleAppointment}
          className="print-hide self-start"
        >
          {user.type === 'guardian' ? tr('schedule') : <IconPlus thicken={30} />}
        </Button>
      )}
      {(!conference.active || isPast(date)) && user.type !== 'teacher' && time.canSchedule && (
        <div className="text-nowrap">{tr('Too late to schedule')}</div>
      )}
      {user.type === 'teacher' && Object.values(time.interpretersAvailable).length > 0 ? (
        <IconGlobe
          tooltip={getInterpreterTooltip(selectedLanguage, time.interpretersAvailable)}
          tooltipOnMobile
          className="text-[1.743rem] opacity-75"
        />
      ) : time.missingInterpreters && !time.markedUnavailable && (!time.scheduled || user.type === 'teacher') ? (
        !conference.interpreterLanguages ||
        conference.interpreterLanguages.find((l) => l === selectedLanguage?.value) ? (
          <IconGlobe tooltipOnMobile tooltip={tr('No interpreters available')} className="text-[1.743rem] text-logo" />
        ) : (
          <IconGlobe
            tooltipOnMobile
            tooltip={tr(
              'This conference has no interpreters for this language, but the district will attempt to find an interpreter.'
            )}
            className="text-[1.743rem] text-warning"
          />
        )
      ) : null}

      {conference.active &&
        (!viewAsAdmin || viewAsOtherTeacher) &&
        !time.scheduled &&
        user.type === 'teacher' &&
        (time.markedUnavailable ? (
          <Button
            shape={ButtonShape.iconSmall}
            tooltip={`Make Available${viewAsAdmin && viewAsOtherTeacher ? ` for ${viewAsOtherTeacher.name}` : ''}`}
            onClick={() => setAvailability(true)}
          >
            <IconClockSolid />
          </Button>
        ) : (
          <Button
            shape={ButtonShape.iconSmall}
            tooltip={`Make Unavailable${viewAsAdmin && viewAsOtherTeacher ? ` for ${viewAsOtherTeacher.name}` : ''}`}
            onClick={() => setAvailability(false)}
          >
            <IconClock />
          </Button>
        ))}
    </div>
  )
}

const getInterpreterTooltip = (
  selectedLanguage: { name: string; value: string } | undefined,
  interpreters: Record<string, number>
) => {
  if (selectedLanguage) {
    return interpreters[selectedLanguage.value]
      ? `${interpreters[selectedLanguage.value]} interpreters available in ${selectedLanguage.name}`
      : 'No interpreters available'
  }
  const languageCount = Object.keys(interpreters).length
  const interpreterCount = Object.values(interpreters).reduce((a, b) => a + b, 0)
  return `${interpreterCount} interpreters available, in ${languageCount} languages`
}

export const ConferenceAppointment: React.FC<{
  conference: SharedConferenceData
  appointment: ConferenceAppointment
  viewAsAdmin?: boolean
  selectedStudent?: BasicUser
  selectedTeacher?: BasicUser
  user: User
  className?: string
  canEdit?: boolean
  reloadSchedule: () => void
}> = ({
  appointment,
  viewAsAdmin,
  user,
  className,
  selectedStudent,
  selectedTeacher,
  conference,
  reloadSchedule,
  canEdit = true,
}) => {
  const { tr } = useTranslation()

  const [attended, setAttended] = React.useState(appointment.attended)
  const [virtual, setVirtual] = React.useState(appointment.virtual)

  const toggleAttended = () => {
    makeRequest('/ptc/appointments/attended', {
      appt_id: appointment.id,
      value: !attended,
    })
      .then(() => setAttended((a) => !a))
      .catch(showAxiosErrorAsToast)
  }

  const deleteAppointment = async () => {
    if (appointment.tooSoonToDeleteLexikeet && user.type != 'teacher') {
      await Popups.simplePopup(
        'You are not allowed to delete this appointment since an interpreter has already been found. Please contact your teacher or administrator.'
      )
      return
    }

    const message =
      user.type === 'teacher' ? (
        <>
          You can schedule the family now, or re-invite them to let them choose a time.
          {appointment.tooSoonToDeleteLexikeet && (
            <b>
              {' '}
              Since this appointment has a lexikeet interpreter and is in the next day, additional costs may apply.
            </b>
          )}
        </>
      ) : (
        tr('You can pick a new time if more are available.')
      )
    if (await Popups.simpleConfirm(<b>{tr('Are you sure you want to cancel this appointment?')}</b>, message))
      makeRequest('/ptc/removeappointment', {
        appt_id: appointment.id,
      })
        .then(reloadSchedule)
        .catch(showAxiosErrorAsToast)
  }

  const toggleLocation = async () => {
    if (
      await Popups.simpleConfirm(
        'Are you sure you want to change this appointment' + (virtual ? ' to in-person?' : ' to virtual?')
      )
    )
      makeRequest('/frontend_api/editAppointmentLocation', {
        appointmentID: appointment.id,
        virtual: !virtual,
      })
        .then(() => setVirtual((v) => !v))
        .catch(showAxiosErrorAsToast)
  }

  return (
    <div className={`flex min-w-0 gap-2 rounded px-2 py-1 shadow-even ${className}`}>
      <div className="flex min-h-[24.4px] min-w-0 flex-grow flex-wrap items-center justify-between gap-x-2 gap-y-1 overflow-hidden text-nowrap">
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex min-w-0 gap-1">
            <SmartLinkOptional
              className={`${!selectedStudent || selectedStudent.id === appointment.student.id ? 'font-bold' : ''} overflow-hidden text-ellipsis no-underline`}
              to={user.type === 'teacher' ? getComposeLink({ students: appointment.student.id }) : undefined}
            >
              {appointment.student.name}
            </SmartLinkOptional>
            {(viewAsAdmin || appointment.teacher.id !== user.id) && (
              <>
                <>+</>
                <div
                  className={`${selectedTeacher?.id === appointment.teacher.id ? 'font-bold' : ''} overflow-hidden text-ellipsis`}
                >
                  {appointment.teacher.name}
                </div>
              </>
            )}
          </div>
          {appointment.languageName && (
            <IconGlobe
              tooltip={`${conference.multilingual ? 'Appointment Interpreted in ' : ''}${appointment.languageName}`}
            />
          )}
          {user.type === 'teacher' && (
            <>
              <HoverPopover
                trigger={
                  virtual ? (
                    <IconVideo
                      opacity={1}
                      onClick={canEdit && conference.virtualOrInPerson === 'both' ? toggleLocation : undefined}
                      className={canEdit && conference.virtualOrInPerson === 'both' ? 'cursor-pointer' : ''}
                    />
                  ) : (
                    <IconLocation
                      opacity={1}
                      onClick={canEdit && conference.virtualOrInPerson === 'both' ? toggleLocation : undefined}
                      className={canEdit && conference.virtualOrInPerson === 'both' ? 'cursor-pointer' : ''}
                    />
                  )
                }
              >
                <div className="flex flex-col gap-1 p-2">
                  <ConferenceLocation
                    location={appointment.location}
                    virtualOrInPerson={virtual ? 'virtual' : 'inperson'}
                  />
                  {canEdit && conference.virtualOrInPerson === 'both' && (
                    <div className="mx-auto text-sm text-link">Click icon to change</div>
                  )}
                </div>
              </HoverPopover>
              <NoteEditor note={appointment.note} appointmentID={appointment.id} canEdit={canEdit} />
              <IconCheckMark
                opacity={attended ? 1 : 0.25}
                thicken={30}
                className={`${canEdit ? 'cursor-pointer' : ''} ${attended ? 'text-green-600' : ''}`}
                tooltip={attended ? 'Attended' : canEdit ? 'Click to mark attended' : undefined}
                onClick={canEdit ? toggleAttended : undefined}
              />
            </>
          )}
        </div>
        {user.type === 'guardian' ? (
          <div className="flex w-full gap-1 text-sm">
            {conference.duration} minutes -
            <ConferenceLocation
              location={appointment.location}
              virtualOrInPerson={virtual ? 'virtual' : 'inperson'}
              className="opacity-75"
            />
            {appointment.multiple && (
              <>
                - <span className="opacity-75">{tr('not shared')}</span>
              </>
            )}
          </div>
        ) : (
          <div className="flex gap-2">
            {appointment.guardians.map((guardian, i) => (
              <ConferenceGuardianBadge key={i} guardian={guardian} />
            ))}
          </div>
        )}
      </div>
      {canEdit && (conference.active || user.type === 'teacher') && !attended && (
        <Button className="print-hide -mr-1 ml-auto" logo shape={ButtonShape.iconSmall} onClick={deleteAppointment}>
          <IconTimes />
        </Button>
      )}
    </div>
  )
}

export const ConferenceGuardianBadge: React.FC<{ guardian: ConferenceAppointment['guardians'][0] }> = ({
  guardian,
}) => (
  <Badge
    badgeStyle={BadgeStyle.guardian}
    tooltip={[guardian.relationship, guardian.phone].filter((s) => s).join(' | ')}
  >
    {guardian.name}
  </Badge>
)

export const ConferenceLocationEdit: React.FC<{
  conference: TeacherConferenceData
  editYourLocation: () => void
}> = ({ conference, editYourLocation }) => (
  <div className="flex flex-col gap-1">
    <b>Your Conference Location</b>
    <ConferenceLocation
      location={conference.yourLocation}
      virtualOrInPerson={conference.virtualOrInPerson}
      redIfMissing
      onClick={editYourLocation}
      className="cursor-pointer"
      icon={<IconEdit opacity={0.2} tooltip="Edit Location" />}
    />
  </div>
)

export const ConferenceLocation: React.FC<{
  location: ConferenceLocation
  virtualOrInPerson: TeacherConferenceData['virtualOrInPerson']
  redIfMissing?: boolean
  className?: string
  onClick?: () => void
  icon?: React.ReactNode
}> = ({ location, virtualOrInPerson, redIfMissing, className, onClick, icon }) => {
  const { tr } = useTranslation()
  const empty = isLocationEmpty(location, virtualOrInPerson)
  const link = location.googleOAuth ? (
    'Google Meets'
  ) : location.url ? (
    <a href={location.url} target="_blank" rel="noreferrer" className="overflow-hidden text-ellipsis">
      {location.url.length > 40 ? location.url.slice(0, 40) + '...' : location.url}
    </a>
  ) : undefined
  const room = location.roomNumber ? `${tr('Room')} ${location.roomNumber}` : undefined
  return (
    <div
      onClick={(e) => {
        if (e.target instanceof HTMLAnchorElement) return
        onClick?.()
      }}
      className={`flex min-w-0 max-w-full items-center gap-1 self-start overflow-hidden text-nowrap ${className} ${empty && redIfMissing ? 'rounded bg-red-500 p-2 text-white' : ''}`}
    >
      {empty ? (
        tr('Location Unknown')
      ) : virtualOrInPerson === 'inperson' && location.lexikeet ? (
        <>
          {room ?? 'Room Unknown'} <span className="flex overflow-hidden">({link})</span>
        </>
      ) : virtualOrInPerson === 'virtual' || !room ? (
        link
      ) : virtualOrInPerson === 'inperson' || !link ? (
        room
      ) : (
        <>
          {room} {tr('or virtually at')} {link}
        </>
      )}
      {icon}
    </div>
  )
}

export const isLocationEmpty = (
  location: ConferenceLocation,
  virtualOrInPerson: TeacherConferenceData['virtualOrInPerson']
) =>
  (virtualOrInPerson === 'virtual' || (!location.roomNumber && !(location.lexikeet && location.url))) &&
  (virtualOrInPerson === 'inperson' || !location.url)

export const NoteEditor: React.FC<{
  note?: string
  appointmentID: number
  canEdit?: boolean
  showFullText?: boolean
}> = ({ note: originalNote, appointmentID, canEdit = true, showFullText }) => {
  const [note, setNote] = React.useState(originalNote)
  const [editing, setEditing] = React.useState(false)
  const [editNote, setEditNote] = React.useState(note || '')
  const [loading, setLoading] = React.useState(false)
  React.useEffect(() => {
    setNote(originalNote)
    setEditNote(originalNote || '')
    setEditing(false)
    setLoading(false)
  }, [originalNote])
  return (
    <Popover
      open={editing}
      trigger={
        showFullText && note ? (
          <div className={`${canEdit ? 'cursor-pointer' : ''}`} onClick={canEdit ? () => setEditing(true) : undefined}>
            {note}
          </div>
        ) : (
          <IconStickyNote
            className={`${canEdit ? 'cursor-pointer' : ''} ${note ? 'text-warning' : ''}`}
            opacity={note ? 1 : 0.25}
            tooltip={note || (canEdit ? 'Add Note' : undefined)}
            onClick={canEdit ? () => setEditing(true) : undefined}
          />
        )
      }
    >
      <TextArea
        value={editNote}
        onChange={setEditNote}
        placeholder="Note"
        className={`${loading ? 'opacity-50' : ''} -p-1 min-w-[300px] !border-none !ring-0`}
        onBlur={() => {
          const newNote = editNote.trim() == '' ? undefined : editNote.trim()
          if (newNote === note) {
            setEditing(false)
            return
          }
          setLoading(true)
          makeRequest('/frontend_api/editAppointmentNote', {
            appointmentID,
            note: newNote,
          })
            .then(() => {
              setEditing(false)
              setNote(newNote)
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      />
    </Popover>
  )
}
